<div class="right-head">
  <h2>{{ title }}</h2>
</div>

@if (!selectedField) {
  <div class="settings-empty-state">
    <h3>Select a field</h3>
    <p>Choose a field on the document to configure its settings.</p>
  </div>
} @else {
  <p class="right-meta">({{ panelOptions.pageMetaLabel }}: {{ selectedField.page }})</p>
  <fieldset class="settings-fieldset" [class.settings-fieldset--signature]="selectedField.type === 'signature'" [disabled]="panelOptions.fieldsetDisabled">
    <section class="settings-block settings-block--top">
      <label for="fieldAssignedTo">{{ panelOptions.actorLabel }}</label>
      <select id="fieldAssignedTo" [ngModel]="selectedField.role" (ngModelChange)="emit({ type: 'update-role', payload: $event })">
        @for (actor of actors; track actor.id) {
          <option [value]="actor.id">{{ actor.label }}</option>
        }
      </select>

      <label class="toggle-row toggle-row--descriptive">
        <input type="checkbox" [checked]="selectedField.required" (change)="emit({ type: 'update-required', payload: $any($event.target).checked })" />
        <span class="toggle-row__text">
          <span class="toggle-row__label">Required</span>
          <span class="toggle-row__hint">This field must be completed before signing.</span>
        </span>
      </label>
    </section>

    <section class="settings-group settings-block settings-block--permissions">
      <h3>Sender actions</h3>
      <div class="toggle-list">
        <label class="toggle-row toggle-row--descriptive">
          <input
            type="checkbox"
            [checked]="selectedField.settings.common.senderActions.allowEditing"
            (change)="emit({ type: 'update-sender-action', payload: { key: 'allowEditing', value: $any($event.target).checked } })"
          />
          <span class="toggle-row__text">
            <span class="toggle-row__label">Allow editing</span>
            <span class="toggle-row__hint">Recipients can change the field value while signing.</span>
          </span>
        </label>
        <label class="toggle-row toggle-row--descriptive">
          <input
            type="checkbox"
            [checked]="selectedField.settings.common.senderActions.allowDeletion"
            (change)="emit({ type: 'update-sender-action', payload: { key: 'allowDeletion', value: $any($event.target).checked } })"
          />
          <span class="toggle-row__text">
            <span class="toggle-row__label">Allow deletion</span>
            <span class="toggle-row__hint">Recipients can remove this field if needed.</span>
          </span>
        </label>
        <label class="toggle-row toggle-row--descriptive">
          <input
            type="checkbox"
            [checked]="selectedField.settings.common.senderActions.requireDefaultValue"
            (change)="emit({ type: 'update-sender-action', payload: { key: 'requireDefaultValue', value: $any($event.target).checked } })"
          />
          <span class="toggle-row__text">
            <span class="toggle-row__label">Require default value</span>
            <span class="toggle-row__hint">Ensure the field starts with a prefilled value.</span>
          </span>
        </label>
      </div>
    </section>

    @if (selectedField.type === 'signature') {

      <section class="settings-block settings-block--identity settings-block--signature">
        <label for="fieldIdInput">ID</label>
        <input id="fieldIdInput" type="text" [ngModel]="selectedField.fieldKey" readonly />
      </section>

      <section class="settings-block settings-block--metadata settings-block--signature">
        <label for="signatureDataSyncTag">Data Sync Tag</label>
        <input
          id="signatureDataSyncTag"
          type="text"
          [ngModel]="selectedField.settings.common.dataSyncTag"
          (ngModelChange)="emit({ type: 'update-data-sync-tag', payload: $event })"
          placeholder="Enter data sync tag"
        />
      </section>

      <section class="settings-block settings-block--placement settings-block--signature">
        <h3>Position</h3>
        <div class="two-col field-pair-grid field-pair-grid--signature">
          <div class="field-pair">
            <label for="positionX">Position X</label>
            <input id="positionX" type="number" min="0" [ngModel]="positionX" (ngModelChange)="emit({ type: 'update-position', payload: { axis: 'x', value: +$event } })" />
          </div>
          <div class="field-pair">
            <label for="positionY">Position Y</label>
            <input id="positionY" type="number" min="0" [ngModel]="positionY" (ngModelChange)="emit({ type: 'update-position', payload: { axis: 'y', value: +$event } })" />
          </div>
        </div>
      </section>

      <section class="settings-block settings-block--navigation settings-block--signature">
        <h3>Tab index</h3>
        <select [ngModel]="selectedField.settings.common.tabIndexMode" (ngModelChange)="emit({ type: 'update-tab-index-mode', payload: $event })">
          <option value="auto">Auto</option>
          <option value="custom">Custom</option>
        </select>
        @if (selectedField.settings.common.tabIndexMode === 'custom') {
          <input type="number" min="1" [ngModel]="selectedField.settings.common.tabIndex || 1" (ngModelChange)="emit({ type: 'update-tab-index', payload: +$event })" />
        }
      </section>

      <section class="settings-block settings-block--typography settings-block--signature font-section">
        <h3>Font</h3>
        <div class="font-stack">
          <select class="font-stack__family" [ngModel]="selectedField.settings.font.family" (ngModelChange)="emit({ type: 'update-font', payload: { family: $event } })">
            @for (family of optionSets.fontFamilyOptions; track family) {
              <option [value]="family">{{ family }}</option>
            }
          </select>
        </div>
        <div class="two-col field-pair-grid field-pair-grid--compact field-pair-grid--signature field-pair-grid--font">
          <div class="field-pair field-pair--font-control">
            <input id="fontSize" type="number" min="1" [ngModel]="selectedField.settings.font.size" (ngModelChange)="emit({ type: 'update-font', payload: { size: +$event } })" />
            <label for="fontSize">Font Size</label>
          </div>
          <div class="field-pair field-pair--font-control">
            <input id="lineHeight" type="number" min="1" [ngModel]="selectedField.settings.font.lineHeight" (ngModelChange)="emit({ type: 'update-font', payload: { lineHeight: +$event } })" />
            <label for="lineHeight">Line Height</label>
          </div>
        </div>
        <div class="toolbar-row toolbar-row--font toolbar-row--signature">
          <div class="toolbar-segment toolbar-segment--font toolbar-segment--font-style">
            <button type="button" class="small-icon small-icon--toolbar" [class.active]="selectedField.settings.font.bold" (click)="toggleExclusiveFontStyle('bold')"><span class="toolbar-glyph">B</span></button>
            <button type="button" class="small-icon small-icon--toolbar" [class.active]="selectedField.settings.font.italic" (click)="toggleExclusiveFontStyle('italic')"><span class="toolbar-glyph">I</span></button>
            <button type="button" class="small-icon small-icon--toolbar small-icon--underline" [class.active]="selectedField.settings.font.underline" (click)="toggleExclusiveFontStyle('underline')"><span class="toolbar-glyph toolbar-glyph--underline">U</span></button>
          </div>
          <div class="font-color-control">
            <button
              #fontColorTrigger
              type="button"
              class="color-chip color-chip--trigger"
              [class.active]="isFontColorPopoverOpen"
              aria-haspopup="dialog"
              [attr.aria-expanded]="isFontColorPopoverOpen"
              aria-label="Choose font color"
              (click)="toggleFontColorPopover()"
            >
              <span class="color-chip__swatch-box">
                <span class="color-chip__swatch" [style.background]="selectedField.settings.font.color"></span>
              </span>
              <span class="color-chip__caret-box" aria-hidden="true">
                <span class="color-chip__caret">▾</span>
              </span>
            </button>
            @if (isFontColorPopoverOpen) {
              <div class="font-color-popover" role="dialog" aria-modal="false" aria-label="Font color picker" (click)="$event.stopPropagation()">
                <div class="font-color-popover__swatches">
                  @for (color of fontColorPresets; track color) {
                    <button
                      type="button"
                      class="font-color-swatch"
                      [class.active]="normalizedDraftFontColor() === color.toLowerCase()"
                      [style.background]="color"
                      [attr.aria-label]="'Use color ' + color"
                      (click)="applyPresetFontColor(color)"
                    ></button>
                  }
                </div>
                <div class="font-color-popover__picker-row">
                  <label class="font-color-picker-shell" for="fontColorSpectrum">
                    <input id="fontColorSpectrum" type="color" [ngModel]="normalizedDraftFontColor()" (ngModelChange)="onFontColorDraftChange($event)" aria-label="Select custom font color" />
                    <span class="font-color-picker-preview" [style.background]="normalizedDraftFontColor()"></span>
                  </label>
                  <input
                    id="fontColorHex"
                    class="font-color-popover__hex"
                    type="text"
                    maxlength="7"
                    [ngModel]="draftFontColor"
                    (ngModelChange)="onFontColorDraftChange($event)"
                    aria-label="Font color hex value"
                  />
                </div>
                <div class="font-color-popover__actions">
                  <button type="button" class="font-color-popover__apply" [disabled]="!canApplyFontColor()" (click)="applyFontColor()">Apply</button>
                  <button type="button" class="font-color-popover__cancel" (click)="cancelFontColorPopover()">Cancel</button>
                </div>
              </div>
            }
          </div>
        </div>
        <div class="toolbar-row toolbar-row--font toolbar-row--signature toolbar-row--font-secondary">
          <div class="toolbar-segment toolbar-segment--font toolbar-segment--align">
            @for (align of optionSets.fontAlignmentOptions; track align) {
              <button type="button" class="small-icon small-icon--toolbar small-icon--align" [class.active]="selectedField.settings.font.align === align" (click)="emit({ type: 'update-font', payload: { align } })">
                {{ align === 'left' ? 'L' : align === 'center' ? 'C' : 'R' }}
              </button>
            }
          </div>
          <label class="toggle-row toggle-row--inline toggle-row--compact toggle-row--font-inline">
            <input type="checkbox" [checked]="selectedField.settings.font.rtl" (change)="emit({ type: 'update-font', payload: { rtl: $any($event.target).checked } })" />
            <span class="toggle-row__label">RTL</span>
          </label>
        </div>
        @if (showCharacterSpacingControl()) {
          <div class="field-pair-grid field-pair-grid--compact field-pair-grid--font-single">
            <div class="field-pair field-pair--font-single field-pair--font-control">
              <input id="fontCharacterSpacing" type="number" [ngModel]="selectedField.settings.font.characterSpacing" (ngModelChange)="emit({ type: 'update-font', payload: { characterSpacing: +$event } })" />
              <label for="fontCharacterSpacing">Character Spacing</label>
            </div>
          </div>
        }
      </section>

      <section class="settings-block settings-block--automation settings-block--signature">
        <h3>Auto positioning</h3>
        <label class="toggle-row toggle-row--compact">
          <input type="checkbox" [checked]="selectedField.settings.common.autoPositioningEnabled" (change)="emit({ type: 'update-auto-positioning-enabled', payload: $any($event.target).checked })" />
          <span class="toggle-row__label">Enable auto positioning</span>
        </label>
        <p class="inspector-meta">The field will be placed next to the configured anchor word</p>
        @if (panelOptions.showAutoPositionConfigureButton) {
          <button type="button" class="secondary-light secondary-light--full" disabled>Configure</button>
        }
      </section>
    } @else {

    @if (selectedField.type !== 'checkbox' && selectedField.type !== 'radio') {
      <section class="settings-block settings-block--identity">
        <label for="fieldIdInput">ID</label>
        <input id="fieldIdInput" type="text" [ngModel]="selectedField.fieldKey" readonly />
      </section>
    }

    @if (selectedField.type === 'textbox') {
      <section class="settings-group">
        <h3>Textbox options</h3>
        <div class="toggle-list toggle-list--compact">
          <label class="toggle-row toggle-row--compact">
            <input type="checkbox" [checked]="selectedField.settings.textbox.readOnly" (change)="emit({ type: 'update-textbox', payload: { readOnly: $any($event.target).checked } })" />
            <span class="toggle-row__label">Read only</span>
          </label>
          <label class="toggle-row toggle-row--compact">
            <input type="checkbox" [checked]="selectedField.settings.textbox.maskValue" (change)="emit({ type: 'update-textbox', payload: { maskValue: $any($event.target).checked } })" />
            <span class="toggle-row__label">Mask value</span>
          </label>
        </div>
        <label for="textboxDefaultText">Default text</label>
        <textarea id="textboxDefaultText" rows="2" [ngModel]="selectedField.settings.textbox.defaultText" (ngModelChange)="emit({ type: 'update-textbox', payload: { defaultText: $event } })"></textarea>
        <label for="textboxHintText">Hint text (optional)</label>
        <input id="textboxHintText" type="text" [ngModel]="selectedField.settings.textbox.hintText" (ngModelChange)="emit({ type: 'update-textbox', payload: { hintText: $event } })" />
        <div class="textbox-settings">
          <div class="textbox-settings__section">
            <label for="textboxResizeType">Resize type</label>
            <p class="textbox-settings__subtext">Resize options.</p>
            <select id="textboxResizeType" [ngModel]="selectedTextboxResizeType()" (ngModelChange)="emit({ type: 'update-textbox', payload: { resizeType: $event } })">
              @for (resizeType of optionSets.textboxResizeOptions; track resizeType) {
                <option [value]="resizeType">{{ textboxResizeLabel(resizeType) }}</option>
              }
            </select>
            <p class="textbox-settings__helper">{{ textboxResizeDescription(selectedTextboxResizeType()) }}</p>
            <div class="textbox-settings__fixed-width-row">
              <label class="toggle-row toggle-row--compact textbox-settings__fixed-width-toggle">
                <input type="checkbox" [checked]="selectedField.settings.textbox.fixedWidth" (change)="emit({ type: 'update-textbox', payload: { fixedWidth: $any($event.target).checked } })" />
                <span class="toggle-row__label">Fixed width</span>
              </label>
              <div class="textbox-settings__limit-column">
                <input
                  id="textboxCharacterLimit"
                  class="textbox-settings__limit-input"
                  type="number"
                  min="1"
                  [disabled]="!selectedField.settings.textbox.fixedWidth"
                  [ngModel]="selectedField.settings.textbox.characterLimit"
                  (ngModelChange)="emit({ type: 'update-textbox', payload: { characterLimit: $event ? +$event : undefined } })"
                />
                <p class="textbox-settings__helper textbox-settings__helper--limit">Set character limit</p>
              </div>
            </div>
          </div>
          <div class="textbox-settings__divider"></div>
          <div class="textbox-settings__section">
            <label for="textboxValidationType">Validation</label>
            <select id="textboxValidationType" [value]="textboxValidationSelection()" (change)="emit({ type: 'update-textbox', payload: { validationType: $any($event.target).value } })">
              @for (option of textboxValidationOptions(); track option) {
                <option [value]="option">{{ textboxValidationLabel(option) }}</option>
              }
            </select>
            @if (shouldShowTextboxValidationValue()) {
              <label for="textboxValidationValue">Validation value</label>
              <input id="textboxValidationValue" type="text" [ngModel]="selectedField.settings.textbox.validationValue" (ngModelChange)="emit({ type: 'update-textbox', payload: { validationValue: $event } })" />
            }
          </div>
        </div>
      </section>
    }

    @if (selectedField.type === 'dropdown') {
      <section class="settings-group">
        <h3>Dropdown options</h3>
        <label for="dropdownHintText">Hint text (optional)</label>
        <input id="dropdownHintText" type="text" [ngModel]="selectedField.settings.dropdown.hintText" (ngModelChange)="emit({ type: 'update-dropdown', payload: { hintText: $event } })" />
        <div class="option-list">
          @for (option of selectedField.settings.dropdown.options; track $index; let i = $index) {
            <div class="option-row option-row--dropdown">
              <button type="button" class="small-icon" (click)="emit({ type: 'move-dropdown-option', payload: { index: i, direction: -1 } })" [disabled]="i === 0">↑</button>
              <button type="button" class="small-icon" (click)="emit({ type: 'move-dropdown-option', payload: { index: i, direction: 1 } })" [disabled]="i === selectedField.settings.dropdown.options.length - 1">↓</button>
              <input type="text" [ngModel]="option" (ngModelChange)="emit({ type: 'update-dropdown-option', payload: { index: i, value: $event } })" />
              <button type="button" class="small-icon" (click)="emit({ type: 'remove-dropdown-option', payload: i })">×</button>
            </div>
          }
        </div>
        <button type="button" class="secondary-light" (click)="emit({ type: 'add-dropdown-option' })">
          {{ panelOptions.showDefaultDropdownOption ? '+ Add value' : '+ Add option' }}
        </button>
        @if (panelOptions.showDefaultDropdownOption) {
          <label for="dropdownDefaultOption">Default dropdown option</label>
          <select id="dropdownDefaultOption" [ngModel]="selectedField.settings.dropdown.defaultOption || ''" (ngModelChange)="emit({ type: 'update-dropdown', payload: { defaultOption: $event || undefined } })">
            <option value="">Select</option>
            @for (option of selectedField.settings.dropdown.options; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        }
      </section>
    }

    @if (selectedField.type === 'checkbox') {
      <section class="settings-group">
        <h3>Checkbox values</h3>
        <label for="checkboxGroupName">ID/Group name</label>
        <input id="checkboxGroupName" type="text" [ngModel]="checkboxGroupName" readonly />
        <div class="option-list">
          @for (option of checkboxGroupValues; track $index; let i = $index) {
            <div class="option-row option-row--choice">
              <label class="choice-toggle" [attr.aria-label]="'Use ' + option + ' as default checkbox value'">
                <input type="checkbox" [checked]="checkboxGroupDefaultValues.includes(option)" (change)="emit({ type: 'toggle-checkbox-default', payload: { option, checked: $any($event.target).checked } })" />
                <span class="choice-toggle__box"></span>
              </label>
              <input type="text" [ngModel]="option" (ngModelChange)="emit({ type: 'update-checkbox-value', payload: { index: i, value: $event } })" />
              <button type="button" class="small-icon" (click)="emit({ type: 'remove-checkbox-value', payload: i })">×</button>
            </div>
          }
        </div>
        <button type="button" class="secondary-light" (click)="emit({ type: 'add-checkbox-value' })">+ Add checkbox</button>
        <label for="checkboxSize">Size</label>
        <input id="checkboxSize" type="number" min="8" max="64" [ngModel]="selectedField.settings.checkbox.size" (ngModelChange)="emit({ type: 'update-checkbox', payload: { size: +$event } })" />
      </section>
    }

    @if (selectedField.type === 'radio') {
      <section class="settings-group">
        <h3>Radio button values</h3>
        <label for="radioGroupName">ID/Group name</label>
        <input id="radioGroupName" type="text" [ngModel]="radioGroupName" readonly />
        <div class="option-list">
          @for (option of radioGroupValues; track $index; let i = $index) {
            <div class="option-row option-row--choice">
              <label class="choice-toggle choice-toggle--radio" [attr.aria-label]="'Use ' + option + ' as default radio value'">
                <input type="checkbox" [checked]="radioGroupDefaultValue === option" (change)="emit({ type: 'toggle-radio-default', payload: { option, checked: $any($event.target).checked } })" />
                <span class="choice-toggle__box"></span>
              </label>
              <input type="text" [ngModel]="option" (ngModelChange)="emit({ type: 'update-radio-value', payload: { index: i, value: $event } })" />
              <button type="button" class="small-icon" (click)="emit({ type: 'remove-radio-value', payload: i })">×</button>
            </div>
          }
        </div>
        <button type="button" class="secondary-light" (click)="emit({ type: 'add-radio-value' })">+ Add radio button</button>
        <label for="radioSize">Size</label>
        <input id="radioSize" type="number" min="8" max="64" [ngModel]="selectedField.settings.radio.size" (ngModelChange)="emit({ type: 'update-radio', payload: { size: +$event } })" />
      </section>
    }

    @if (selectedField.type === 'date_signed' || selectedField.type === 'editable_date') {
      <section class="settings-group">
        <h3>Date format settings</h3>
        <label for="dateFormatSelect">Date format</label>
        <select id="dateFormatSelect" [ngModel]="selectedField.settings.date.dateFormat" (ngModelChange)="emit({ type: 'update-date', payload: { dateFormat: $event } })">
          @for (format of optionSets.dateFormatOptions; track format) {
            <option [value]="format">{{ format }}</option>
          }
        </select>
        <label for="timeFormatSelect">Time format</label>
        <select id="timeFormatSelect" [ngModel]="selectedField.settings.date.timeFormat" (ngModelChange)="emit({ type: 'update-date', payload: { timeFormat: $event } })">
          @for (format of optionSets.timeFormatOptions; track format) {
            <option [value]="format">{{ format }}</option>
          }
        </select>
        <label for="defaultDateInput">Default date</label>
        <input id="defaultDateInput" type="text" [ngModel]="selectedField.settings.date.defaultDate" (ngModelChange)="emit({ type: 'update-date', payload: { defaultDate: $event } })" placeholder="Select default date" />
      </section>
    }

    @if (selectedField.type === 'image') {
      <section class="settings-group">
        <h3>Image settings</h3>
        <label for="imageTitleInput">Image title</label>
        <input id="imageTitleInput" type="text" [ngModel]="selectedField.settings.image.title" (ngModelChange)="emit({ type: 'update-image', payload: { title: $event } })" />
        <label for="imageInstructionInput">Instruction (optional)</label>
        <textarea id="imageInstructionInput" rows="2" [ngModel]="selectedField.settings.image.instruction" (ngModelChange)="emit({ type: 'update-image', payload: { instruction: $event } })"></textarea>
        @if (panelOptions.showAddDefaultImageButton) {
          <button type="button" class="secondary-light" disabled>Add default image</button>
        }
        @if (panelOptions.showImageAcceptOptions) {
          <p class="muted">Accept only</p>
          <label class="toggle-row toggle-row--compact">
            <input type="checkbox" [checked]="selectedField.settings.image.acceptJpgJpeg" (change)="emit({ type: 'update-image', payload: { acceptJpgJpeg: $any($event.target).checked } })" />
            <span class="toggle-row__label">JPG/JPEG</span>
          </label>
          <label class="toggle-row toggle-row--compact">
            <input type="checkbox" [checked]="selectedField.settings.image.acceptSvg" (change)="emit({ type: 'update-image', payload: { acceptSvg: $any($event.target).checked } })" />
            <span class="toggle-row__label">SVG</span>
          </label>
          <label class="toggle-row toggle-row--compact">
            <input type="checkbox" [checked]="selectedField.settings.image.acceptPng" (change)="emit({ type: 'update-image', payload: { acceptPng: $any($event.target).checked } })" />
            <span class="toggle-row__label">PNG</span>
          </label>
          <label class="toggle-row toggle-row--compact">
            <input type="checkbox" [checked]="selectedField.settings.image.acceptBmp" (change)="emit({ type: 'update-image', payload: { acceptBmp: $any($event.target).checked } })" />
            <span class="toggle-row__label">BMP</span>
          </label>
        }
      </section>
    }

    <section class="settings-group">
      <h3>Data Sync Tag</h3>
      <input type="text" [ngModel]="selectedField.settings.common.dataSyncTag" (ngModelChange)="emit({ type: 'update-data-sync-tag', payload: $event })" placeholder="Enter data sync tag" />
    </section>

    <section class="settings-group settings-block settings-block--placement">
      <h3>Position</h3>
      <div class="two-col field-pair-grid">
        <div class="field-pair">
          <label for="positionX">Position X</label>
          <input id="positionX" type="number" min="0" [ngModel]="positionX" (ngModelChange)="emit({ type: 'update-position', payload: { axis: 'x', value: +$event } })" />
        </div>
        <div class="field-pair">
          <label for="positionY">Position Y</label>
          <input id="positionY" type="number" min="0" [ngModel]="positionY" (ngModelChange)="emit({ type: 'update-position', payload: { axis: 'y', value: +$event } })" />
        </div>
      </div>
    </section>

    <section class="settings-group settings-block settings-block--navigation">
      <h3>Tab index</h3>
      <select [ngModel]="selectedField.settings.common.tabIndexMode" (ngModelChange)="emit({ type: 'update-tab-index-mode', payload: $event })">
        <option value="auto">Auto</option>
        <option value="custom">Custom</option>
      </select>
      @if (selectedField.settings.common.tabIndexMode === 'custom') {
        <input type="number" min="1" [ngModel]="selectedField.settings.common.tabIndex || 1" (ngModelChange)="emit({ type: 'update-tab-index', payload: +$event })" />
      }
    </section>

    <section class="settings-group font-section">
      <h3>Font</h3>
      <div class="font-stack">
        <select class="font-stack__family" [ngModel]="selectedField.settings.font.family" (ngModelChange)="emit({ type: 'update-font', payload: { family: $event } })">
          @for (family of optionSets.fontFamilyOptions; track family) {
            <option [value]="family">{{ family }}</option>
          }
        </select>
      </div>
      <div class="two-col field-pair-grid field-pair-grid--compact field-pair-grid--font">
        <div class="field-pair field-pair--font-control">
          <input id="fontSize" type="number" min="1" [ngModel]="selectedField.settings.font.size" (ngModelChange)="emit({ type: 'update-font', payload: { size: +$event } })" />
          <label for="fontSize">Font Size</label>
        </div>
        <div class="field-pair field-pair--font-control">
          <input id="lineHeight" type="number" min="1" [ngModel]="selectedField.settings.font.lineHeight" (ngModelChange)="emit({ type: 'update-font', payload: { lineHeight: +$event } })" />
          <label for="lineHeight">Line Height</label>
        </div>
      </div>
      <div class="toolbar-row toolbar-row--font">
        <div class="toolbar-segment toolbar-segment--font toolbar-segment--font-style">
          <button type="button" class="small-icon small-icon--toolbar" [class.active]="selectedField.settings.font.bold" (click)="toggleExclusiveFontStyle('bold')"><span class="toolbar-glyph">B</span></button>
          <button type="button" class="small-icon small-icon--toolbar" [class.active]="selectedField.settings.font.italic" (click)="toggleExclusiveFontStyle('italic')"><span class="toolbar-glyph">I</span></button>
          <button type="button" class="small-icon small-icon--toolbar small-icon--underline" [class.active]="selectedField.settings.font.underline" (click)="toggleExclusiveFontStyle('underline')"><span class="toolbar-glyph toolbar-glyph--underline">U</span></button>
        </div>
        <div class="font-color-control">
          <button
            #fontColorTrigger
            type="button"
            class="color-chip color-chip--trigger"
            [class.active]="isFontColorPopoverOpen"
            aria-haspopup="dialog"
            [attr.aria-expanded]="isFontColorPopoverOpen"
            aria-label="Choose font color"
            (click)="toggleFontColorPopover()"
          >
            <span class="color-chip__swatch-box">
              <span class="color-chip__swatch" [style.background]="selectedField.settings.font.color"></span>
            </span>
            <span class="color-chip__caret-box" aria-hidden="true">
              <span class="color-chip__caret">▾</span>
            </span>
          </button>
          @if (isFontColorPopoverOpen) {
            <div class="font-color-popover" role="dialog" aria-modal="false" aria-label="Font color picker" (click)="$event.stopPropagation()">
              <div class="font-color-popover__swatches">
                @for (color of fontColorPresets; track color) {
                  <button
                    type="button"
                    class="font-color-swatch"
                    [class.active]="normalizedDraftFontColor() === color.toLowerCase()"
                    [style.background]="color"
                    [attr.aria-label]="'Use color ' + color"
                    (click)="applyPresetFontColor(color)"
                  ></button>
                }
              </div>
              <div class="font-color-popover__picker-row">
                <label class="font-color-picker-shell" for="fontColorSpectrum">
                  <input id="fontColorSpectrum" type="color" [ngModel]="normalizedDraftFontColor()" (ngModelChange)="onFontColorDraftChange($event)" aria-label="Select custom font color" />
                  <span class="font-color-picker-preview" [style.background]="normalizedDraftFontColor()"></span>
                </label>
                <input
                  id="fontColorHex"
                  class="font-color-popover__hex"
                  type="text"
                  maxlength="7"
                  [ngModel]="draftFontColor"
                  (ngModelChange)="onFontColorDraftChange($event)"
                  aria-label="Font color hex value"
                />
              </div>
              <div class="font-color-popover__actions">
                <button type="button" class="font-color-popover__apply" [disabled]="!canApplyFontColor()" (click)="applyFontColor()">Apply</button>
                <button type="button" class="font-color-popover__cancel" (click)="cancelFontColorPopover()">Cancel</button>
              </div>
            </div>
          }
        </div>
      </div>
      <div class="toolbar-row toolbar-row--font toolbar-row--font-secondary">
        <div class="toolbar-segment toolbar-segment--font toolbar-segment--align">
          @for (align of optionSets.fontAlignmentOptions; track align) {
            <button type="button" class="small-icon small-icon--toolbar small-icon--align" [class.active]="selectedField.settings.font.align === align" (click)="emit({ type: 'update-font', payload: { align } })">
              {{ align === 'left' ? 'L' : align === 'center' ? 'C' : 'R' }}
            </button>
          }
        </div>
        <label class="toggle-row toggle-row--inline toggle-row--compact toggle-row--font-inline">
          <input type="checkbox" [checked]="selectedField.settings.font.rtl" (change)="emit({ type: 'update-font', payload: { rtl: $any($event.target).checked } })" />
          <span class="toggle-row__label">RTL</span>
        </label>
      </div>
      @if (showCharacterSpacingControl()) {
        <div class="field-pair-grid field-pair-grid--compact field-pair-grid--font-single">
          <div class="field-pair field-pair--font-single field-pair--font-control">
            <input id="fontCharacterSpacing" type="number" [ngModel]="selectedField.settings.font.characterSpacing" (ngModelChange)="emit({ type: 'update-font', payload: { characterSpacing: +$event } })" />
            <label for="fontCharacterSpacing">Character Spacing</label>
          </div>
        </div>
      }
    </section>

    <section class="settings-group settings-block settings-block--automation">
      <h3>Auto positioning</h3>
      <label class="toggle-row toggle-row--descriptive">
        <input type="checkbox" [checked]="selectedField.settings.common.autoPositioningEnabled" (change)="emit({ type: 'update-auto-positioning-enabled', payload: $any($event.target).checked })" />
        <span class="toggle-row__text">
          <span class="toggle-row__label">Enable auto positioning</span>
          <span class="toggle-row__hint">Place the field next to the configured anchor word.</span>
        </span>
      </label>
      @if (panelOptions.showAutoPositionConfigureButton) {
        <button type="button" class="secondary-light" disabled>Configure</button>
      }
    </section>

    @if (panelOptions.showConditionalLogicSection) {
      <section class="settings-group">
        <h3>Conditional logic</h3>
        <button type="button" class="secondary-light" disabled>Configure</button>
      </section>
    }
    }
  </fieldset>
}
